import { useState, useRef, useEffect, type ComponentType } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import type { Language } from '../types';

// SVG Flag components for cross-platform support
const TurkeyFlag = () => (
  <svg viewBox="0 0 30 20" className="lang-flag-svg">
    <rect width="30" height="20" fill="#E30A17"/>
    <circle cx="9.5" cy="10" r="5" fill="white"/>
    <circle cx="10.75" cy="10" r="4" fill="#E30A17"/>
    <polygon
      fill="white"
      points="16.8,7 17.7,9.6 20.5,9.6 18.2,11.3 19.1,14 16.8,12.2 14.5,14 15.4,11.3 13.1,9.6 15.9,9.6"
    />
  </svg>
);

const UKFlag = () => (
  <svg viewBox="0 0 32 24" className="lang-flag-svg">
    <rect width="32" height="24" fill="#012169"/>
    <path d="M0,0 L32,24 M32,0 L0,24" stroke="white" strokeWidth="4"/>
    <path d="M0,0 L32,24 M32,0 L0,24" stroke="#C8102E" strokeWidth="2.5"/>
    <path d="M16,0 V24 M0,12 H32" stroke="white" strokeWidth="6"/>
    <path d="M16,0 V24 M0,12 H32" stroke="#C8102E" strokeWidth="4"/>
  </svg>
);

const FranceFlag = () => (
  <svg viewBox="0 0 30 20" className="lang-flag-svg">
    <rect width="10" height="20" fill="#002654"/>
    <rect x="10" width="10" height="20" fill="#FFFFFF"/>
    <rect x="20" width="10" height="20" fill="#CE1126"/>
  </svg>
);

const GermanyFlag = () => (
  <svg viewBox="0 0 30 20" className="lang-flag-svg">
    <rect width="30" height="6.67" fill="#000000"/>
    <rect y="6.67" width="30" height="6.67" fill="#DD0000"/>
    <rect y="13.33" width="30" height="6.67" fill="#FFCE00"/>
  </svg>
);

const languages: { code: Language; name: string; Flag: ComponentType }[] = [
  { code: 'tr', name: 'Türkçe', Flag: TurkeyFlag },
  { code: 'en', name: 'English', Flag: UKFlag },
  { code: 'fr', name: 'Français', Flag: FranceFlag },
  { code: 'de', name: 'Deutsch', Flag: GermanyFlag },
];

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find(l => l.code === language) || languages[0];
  const CurrentFlag = currentLang.Flag;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: Language) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="lang-dropdown" ref={dropdownRef}>
      <button
        className="lang-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <CurrentFlag />
        <span className="lang-code">{currentLang.code.toUpperCase()}</span>
        <svg
          className={`lang-chevron ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="lang-dropdown-menu" role="listbox">
          {languages.map((lang) => {
            const Flag = lang.Flag;
            return (
              <button
                key={lang.code}
                className={`lang-dropdown-item ${language === lang.code ? 'active' : ''}`}
                onClick={() => handleSelect(lang.code)}
                role="option"
                aria-selected={language === lang.code}
              >
                <Flag />
                <span className="lang-name">{lang.name}</span>
                {language === lang.code && (
                  <svg className="lang-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M11.5 4L5.5 10L2.5 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}

      <style>{`
        .lang-dropdown {
          position: relative;
        }

        .lang-dropdown-trigger {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: white;
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .lang-dropdown-trigger:hover {
          border-color: #d1d5db;
          background: #f9fafb;
        }

        .lang-dropdown-trigger:focus {
          outline: none;
          border-color: #c9a962;
          box-shadow: 0 0 0 3px rgba(201, 169, 98, 0.1);
        }

        .lang-flag-svg {
          width: 22px;
          height: 16px;
          border-radius: 3px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          flex-shrink: 0;
        }

        .lang-code {
          font-size: 0.8rem;
          font-weight: 600;
          color: #374151;
          letter-spacing: 0.02em;
        }

        .lang-chevron {
          color: #9ca3af;
          transition: transform 0.2s ease;
        }

        .lang-chevron.open {
          transform: rotate(180deg);
        }

        .lang-dropdown-menu {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          min-width: 160px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
          padding: 6px;
          z-index: 1000;
          animation: dropdownFadeIn 0.15s ease-out;
        }

        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lang-dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 12px;
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s ease;
          font-family: inherit;
          text-align: left;
        }

        .lang-dropdown-item:hover {
          background: #f3f4f6;
        }

        .lang-dropdown-item.active {
          background: #fef9ed;
        }

        .lang-dropdown-item .lang-flag-svg {
          width: 24px;
          height: 18px;
        }

        .lang-name {
          flex: 1;
          font-size: 0.9rem;
          font-weight: 500;
          color: #1f2937;
        }

        .lang-check {
          color: #c9a962;
        }

        /* Mobile adjustments */
        @media (max-width: 768px) {
          .lang-dropdown-trigger {
            padding: 6px 10px;
          }

          .lang-code {
            display: none;
          }

          .lang-dropdown-menu {
            right: -10px;
          }
        }
      `}</style>
    </div>
  );
}
